import React, { useState, useEffect, useMemo } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { FaThLarge, FaChevronDown, FaSignOutAlt, FaCogs, FaTimes } from 'react-icons/fa';
import * as Icons from 'react-icons/fa';
import { useAdminAuth } from '../context/AdminAuthContext';
import logo from '../../assets/TradyFroglogo.png';

// Define Props for Responsiveness
interface AdminSidebarProps {
  isOpen: boolean;
  onClose: () => void;
}

const AdminSidebar: React.FC<AdminSidebarProps> = ({ isOpen, onClose }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const { logout, role, permissions } = useAdminAuth(); 
  const [menuGroups, setMenuGroups] = useState<any[]>([]);
  const [openMenu, setOpenMenu] = useState<string | null>(null);

  const savedPermissions = useMemo(() => {
    try {
      return typeof permissions === 'string' ? JSON.parse(permissions || "{}") : (permissions || {});
    } catch (e) {
      return {};
    }
  }, [permissions]);

  useEffect(() => {
    fetch("/nodejs/api/sidebar/all")
      .then(res => res.json())
      .then(data => setMenuGroups(Array.isArray(data) ? data : []))
      .catch(err => console.error("Node.js sidebar fetch failed", err));
  }, []);

  // Auto-close sidebar on mobile when route changes
  useEffect(() => {
    if (window.innerWidth < 1024) {
      onClose();
    }
  }, [location.pathname]);

  const renderIcon = (iconName: string) => {
    const IconComponent = (Icons as any)[iconName];
    return IconComponent ? <IconComponent size={18} /> : <Icons.FaBox size={18} />;
  };

  return (
    <>
      <style>{`
        .no-scrollbar::-webkit-scrollbar { display: none; }
        .no-scrollbar { -ms-overflow-style: none; scrollbar-width: none; }
      `}</style>

      {/* Mobile Backdrop Overlay */}
      {isOpen && (
        <div 
          className="fixed inset-0 bg-black/40 z-[45] lg:hidden backdrop-blur-sm transition-opacity"
          onClick={onClose}
        />
      )}

      {/* Sidebar Container */}
      <aside 
        className={`fixed left-0 top-0 h-screen w-64 bg-white border-r border-dashed border-gray-200 flex flex-col z-[50] transition-transform duration-300 ease-in-out 
          ${isOpen ? 'translate-x-0' : '-translate-x-full'} 
          lg:translate-x-0 shadow-2xl lg:shadow-none`}
      >
        {/* Header Logo Area */}
        <div className="p-6 shrink-0 flex items-center justify-between border-b border-gray-50">
          <div
  className="flex items-center"
  onClick={() => navigate('/frog-side/dashboard')}
>

  {/* Logo */}
  <img
    src= {logo}   // public folder me logo rakho
    alt="TradyFrog Logo"
   className="h-12 w-auto md:h-12 lg:h-12 object-contain transition-all duration-300 drop-shadow-md"
  />

  {/* Text */}
  <span className="text-2xl font-black text-gray-900 tracking-tighter">
    TRADY
    <span className="text-[#006d5b]">FROG</span>
  </span>

</div>

           {/* Close Button (Mobile Only) */}
           <button onClick={onClose} className="lg:hidden text-gray-400 hover:text-red-500 transition-colors">
             <FaTimes size={20} />
           </button>
        </div>

        {/* Navigation Links */}
        <nav className="flex-1 overflow-y-auto px-4 py-6 no-scrollbar">
          <ul className="space-y-1">
            <li>
              <Link to="/frog-side/dashboard" className={`flex items-center gap-3 px-4 py-3 rounded-lg text-xs font-bold tracking-wide transition-all ${location.pathname === '/frog-side/dashboard' ? 'bg-[#006d5b] text-white shadow-md shadow-[#006d5b]/20' : 'text-gray-500 hover:bg-gray-50 hover:text-gray-900'}`}>
                <FaThLarge size={16} /> DASHBOARD
              </Link>
            </li>

            {(role === 'SUPER_ADMIN' || role === 'ADMIN') && (
              <li>
                 <Link to="/frog-side/add-sidebar-menu" className={`flex items-center gap-3 px-4 py-3 rounded-lg text-xs font-bold transition-all ${location.pathname === '/frog-side/add-sidebar-menu' ? 'bg-orange-50 text-orange-600' : 'text-gray-500 hover:bg-orange-50 hover:text-orange-600'}`}>
                    <FaCogs size={16} /> SYSTEM SETTINGS
                 </Link>
              </li>
            )}

            <div className="pt-4 pb-2">
              <p className="px-4 text-[10px] font-black text-gray-300 uppercase tracking-widest">Modules</p>
            </div>

            {menuGroups.map((group) => {
              const isGroupVisible = role === 'SUPER_ADMIN' || role === 'ADMIN' || !!savedPermissions[group.title];
              if (!isGroupVisible) return null;

              const allowedPages = group.subItems.filter((sub: any) => {
                if (role === 'SUPER_ADMIN' || role === 'ADMIN') return true;
                return !!savedPermissions[sub.path];
              });

              if (allowedPages.length === 0 && role !== 'ADMIN' && role !== 'SUPER_ADMIN') return null;

              const isOpenGroup = openMenu === group.title;

              return (
                <li key={group.id || group.title}>
                  {/* UPDATED BUTTON STYLE HERE */}
                  <button 
                    onClick={() => setOpenMenu(isOpenGroup ? null : group.title)} 
                    className={`w-full flex items-center justify-between px-4 py-3 rounded-lg text-xs font-bold tracking-wide transition-all 
                      ${isOpenGroup 
                        ? 'bg-[#006d5b] text-white shadow-md shadow-[#006d5b]/20'  // Active Style (Same as Dashboard)
                        : 'text-gray-500 hover:bg-gray-50 hover:text-gray-900'     // Inactive Style
                      }`}
                  >
                    <div className="flex items-center gap-3">
                      {/* Icon Color Logic */}
                      <span className={isOpenGroup ? 'text-white' : 'text-gray-400'}>
                        {renderIcon(group.icon)}
                      </span>
                      {group.title}
                    </div>
                    {/* Chevron Color Logic */}
                    <FaChevronDown 
                      size={10} 
                      className={`transition-transform duration-300 ${isOpenGroup ? 'rotate-180 text-white' : 'text-gray-400'}`} 
                    />
                  </button>

                  <AnimatePresence>
                    {isOpenGroup && (
                      <motion.ul initial={{ opacity: 0, height: 0 }} animate={{ opacity: 1, height: 'auto' }} exit={{ opacity: 0, height: 0 }} className="overflow-hidden">
                        {allowedPages.map((sub: any, index: number) => (
                          <li key={`${group.title}-${index}`} className="pl-4 pr-1 py-1">
                            <Link to={sub.path} className={`block px-4 py-2.5 rounded-lg text-[11px] font-bold transition-all flex items-center gap-2 ${location.pathname === sub.path ? 'text-[#006d5b] bg-[#e0f2f1] shadow-sm' : 'text-gray-400 hover:text-[#006d5b] hover:pl-6'}`}>
                              <div className={`w-1.5 h-1.5 rounded-full ${location.pathname === sub.path ? 'bg-[#006d5b]' : 'bg-gray-300'}`}></div>
                              {sub.name}
                            </Link>
                          </li>
                        ))}
                      </motion.ul>
                    )}
                  </AnimatePresence>
                </li>
              );
            })}
          </ul>
        </nav>

        <div className="p-4 border-t border-gray-50 shrink-0">
          <button onClick={logout} className="flex items-center gap-3 text-red-500 font-bold text-xs tracking-widest hover:bg-red-50 rounded-lg px-4 py-3 w-full transition-all">
            <FaSignOutAlt /> LOGOUT
          </button>
        </div>
      </aside>
    </>
  );
};

export default AdminSidebar;